import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { itemTitle, type PublicItemSummary, type PublicSaleResponse } from '@sellit/shared';
import { NotFound } from '../App';
import { api, ApiError } from '../lib/api';
import { formatDeadline, setPageTitle } from '../lib/format';
import { Icon } from '../lib/Icon';
import { PoweredBy, PreviewNotice, Wordmark } from '../lib/Brand';

export function SalePage() {
  const { slug = '' } = useParams();
  const [data, setData] = useState<PublicSaleResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    api.publicSale(slug).then(
      (d) => {
        setData(d);
        setPageTitle(d.sale.title);
      },
      setError,
    );
  }, [slug]);

  if (error?.status === 404) return <NotFound message="This sale doesn't exist or has ended." />;
  if (error) return <main className="page"><p className="error">{error.message}</p></main>;
  if (!data) return <main className="page center muted">Loading…</main>;

  const { sale, items } = data;
  const forSale = items.filter((i) => i.status !== 'SOLD');
  const sold = items.filter((i) => i.status === 'SOLD');
  const deadline = formatDeadline(sale.endsAt, sale.timezone);

  return (
    <>
      <header className="hero">
        <div className="hero-inner">
          <Wordmark />
          <h1>{sale.title}</h1>
          <ul className="meta-chips">
            <li><Icon name="pin" size={16} /> {sale.publicLocation}</li>
            {deadline && <li><Icon name="calendar" size={16} /> Ends {deadline}</li>}
            <li><Icon name="tag" size={16} /> {forSale.length} item{forSale.length === 1 ? '' : 's'} available</li>
          </ul>
          {sale.description && <p className="hero-desc prewrap">{sale.description}</p>}
        </div>
      </header>

      <main className="page wide sale-body">
        {sale.preview && <PreviewNotice status={sale.preview} />}
        {sale.status === 'CLOSED' && !sale.preview && (
          <p className="notice"><Icon name="info" /> This sale has ended. Thanks for stopping by!</p>
        )}
        {sale.contactNote && (
          <div className="contact-card">
            <Icon name="message" />
            <div>
              <strong>How to buy</strong>
              <p className="prewrap">{sale.contactNote}</p>
            </div>
          </div>
        )}

        {forSale.length === 0 ? (
          <p className="empty muted">No items are listed right now. Check back soon.</p>
        ) : (
          <Grid slug={sale.slug} items={forSale} />
        )}

        {sold.length > 0 && (
          <details className="sold">
            <summary>Already sold ({sold.length})</summary>
            <Grid slug={sale.slug} items={sold} />
          </details>
        )}
      </main>
      <PoweredBy />
    </>
  );
}

function Grid({ slug, items }: { slug: string; items: PublicItemSummary[] }) {
  return (
    <ul className="grid">
      {items.map((item) => (
        <li key={item.number}>
          <Link to={`/${slug}/item/${item.number}`} className={`tile ${item.status.toLowerCase()}`}>
            <div className="tile-img">
              {item.thumbUrl ? <img src={item.thumbUrl} alt={itemTitle(item)} loading="lazy" /> : <div className="noimg" />}
              {item.status !== 'AVAILABLE' && <span className={`ribbon ${item.status.toLowerCase()}`}>{item.status === 'SOLD' ? 'Sold' : 'Pending'}</span>}
              {item.pricingMode === 'FREE' && item.status === 'AVAILABLE' && <span className="ribbon free">Free</span>}
            </div>
            <div className="tile-text">
              <span className="price">{item.priceLabel}</span>
              <span className="title">
                <span className="num">#{item.number}</span> {item.title}
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
